import { useState } from "react";
import { toast } from "sonner";
import { Plus, Search, KeyRound, Pencil, Trash2, Lock, Unlock } from "lucide-react";
import { api } from "@/lib/api";
import { CARD, FormDialog, Loading, Pill, Table, Title, confirmRun, fmtDay, fmtDT, useData } from "@/ops/ui";

export const EMP_FIELDS = [
  { k: "name", label: "Ad Soyad", required: true }, { k: "phone", label: "Telefon (giriş için)", required: true, type: "tel" },
  { k: "email", label: "E-posta", type: "email", empty: null }, { k: "title", label: "Görev / Unvan" },
  { k: "hire_date", label: "İşe giriş tarihi", type: "date" }, { k: "leave_date", label: "İşten ayrılış tarihi", type: "date" },
  { k: "card_type", label: "Kimlik kartı türü", type: "select", options: [["", "Seçilmedi"], ["silahli", "Silahlı"], ["silahsiz", "Silahsız"]] },
  { k: "card_no", label: "Kimlik kartı numarası" }, { k: "card_expiry", label: "Kart son geçerlilik", type: "date" },
  { k: "notes", label: "Notlar", type: "textarea" }, { k: "active", label: "Aktif personel", type: "bool" },
];
const PW_FIELDS = [{ k: "password", label: "Geçici şifre (en az 10 karakter)", required: true, wide: true, hint: "Personel ilk girişte bu şifreyi değiştirmek zorundadır. Şifreyi personele güvenli bir yoldan iletin." }];

function AccountCell({ account }) {
  if (!account) return <span className="text-zinc-400">Hesap yok</span>;
  let label = "Açık";
  if (!account.active) label = "Kapalı";
  else if (account.must_change) label = "Şifre bekliyor";
  return <><Pill ok={account.active}>{label}</Pill><p className="mt-1 text-zinc-500">Son giriş: {fmtDT(account.last_login_at)}</p></>;
}

function RowActions({ e, edit, account, reload }) {
  const acc = e.account;
  const toggle = () => confirmRun(acc.active ? "Bu personelin giriş erişimi kapatılsın mı?" : "Giriş erişimi açılsın mı?",
    async () => { await api.patch(`/ops/admin/users/${acc.id}`, { active: !acc.active }); reload(); }, "Erişim güncellendi");
  const del = () => confirmRun(`${e.name} silinsin mi? Giriş hesabı da kapatılır.`, async () => { await api.delete(`/ops/admin/employees/${e.id}`); reload(); }, "Personel silindi");
  return (
    <td className="whitespace-nowrap px-3 py-2">
      <button onClick={() => edit(e)} className="rounded p-1.5 hover:bg-zinc-100" title="Düzenle" data-testid={`employee-edit-${e.id}`}><Pencil className="h-4 w-4" /></button>
      <button onClick={() => account(e)} className="rounded p-1.5 hover:bg-zinc-100" title={acc ? "Şifre sıfırla" : "Giriş hesabı aç"} data-testid={`employee-account-${e.id}`}><KeyRound className="h-4 w-4" /></button>
      {acc && <button onClick={toggle} className="rounded p-1.5 hover:bg-zinc-100" title="Erişimi aç/kapat" data-testid={`employee-access-${e.id}`}>{acc.active ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}</button>}
      <button onClick={del} className="rounded p-1.5 text-red-700 hover:bg-red-50" title="Sil" data-testid={`employee-delete-${e.id}`}><Trash2 className="h-4 w-4" /></button>
    </td>
  );
}

const expiresSoon = (d) => !!d && new Date(d) - new Date() < 60 * 864e5;

function Row({ e, edit, account, reload }) {
  return (
    <tr data-testid={`employee-row-${e.id}`}>
      <td className="whitespace-nowrap px-3 py-2 font-semibold text-[color:var(--red)]">{e.sicil_no}</td>
      <td className="px-3 py-2 font-semibold">{e.name}<p className="text-xs font-normal text-zinc-500">{e.title}</p></td>
      <td className="whitespace-nowrap px-3 py-2">{e.phone}</td>
      <td className="whitespace-nowrap px-3 py-2">{CARD[e.card_type] || "-"} {e.card_no && <span className="text-xs text-zinc-500">· {e.card_no}</span>}</td>
      <td className={`whitespace-nowrap px-3 py-2 ${expiresSoon(e.card_expiry) ? "font-semibold text-orange-700" : ""}`}>{fmtDay(e.card_expiry)}</td>
      <td className="px-3 py-2"><Pill ok={e.active}>{e.active ? "Aktif" : "Pasif"}</Pill></td>
      <td className="px-3 py-2 text-xs"><AccountCell account={e.account} /></td>
      <RowActions e={e} edit={edit} account={account} reload={reload} />
    </tr>
  );
}

const EMP_HEAD = ["Sicil No", "Ad Soyad", "Telefon", "Kimlik Kartı", "Kart Bitiş", "Durum", "Giriş Hesabı", ""];
const NO_INITIAL = {};

export default function Employees() {
  const [q, setQ] = useState("");
  const [rows, reload] = useData("/ops/admin/employees", { q });
  const [form, setForm] = useState(null);
  const [acc, setAcc] = useState(null);
  const save = (v) => (form.id ? api.put(`/ops/admin/employees/${form.id}`, v) : api.post("/ops/admin/employees", v)).then(() => toast.success("Personel kaydedildi"));
  const setPw = (v) => api.post(`/ops/admin/employees/${acc.id}/account`, v).then(() => toast.success("Giriş hesabı hazır. Personel telefon numarası ve geçici şifreyle giriş yapabilir."));
  return (
    <div>
      <Title title="Personel" desc={rows ? `${rows.length} personel` : ""}>
        <button onClick={() => setForm({ active: true, title: "Güvenlik Görevlisi" })} className="btn-red !py-2" data-testid="employee-add-btn"><Plus className="h-4 w-4" /> Personel Ekle</button>
      </Title>
      <div className="relative mb-4 max-w-md"><Search className="absolute left-3 top-3.5 h-4 w-4 text-zinc-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ad, telefon veya sicil no..." className="field-input !pl-9" data-testid="employee-search" /></div>
      {!rows ? <Loading /> : (
        <Table head={EMP_HEAD} testid="employees-table">
          {rows.map((e) => <Row key={e.id} e={e} edit={setForm} account={setAcc} reload={reload} />)}
          {!rows.length && <tr><td colSpan={8} className="p-6 text-center text-zinc-500" data-testid="employees-empty">Henüz personel eklenmedi.</td></tr>}
        </Table>
      )}
      <FormDialog open={!!form} title={form?.id ? "Personeli Düzenle" : "Yeni Personel"} fields={EMP_FIELDS} initial={form} onSubmit={save} onClose={(ok) => { setForm(null); if (ok) reload(); }} />
      <FormDialog open={!!acc} title={acc?.account ? `${acc?.name} – Şifre Sıfırla` : `${acc?.name} – Giriş Hesabı Aç`} fields={PW_FIELDS} initial={NO_INITIAL} onSubmit={setPw} onClose={(ok) => { setAcc(null); if (ok) reload(); }} />
    </div>
  );
}
